export type StuckCauseKey = "reconnect" | "retry" | "pending" | "never_tried";

interface StuckLike {
  id: string;
  marketplace: string;
  awb_attempts: number;
  awb_last_error: string | null | undefined;
  /** now - picked_at. */
  stuck_seconds: number;
}

/** Longest a picked order may sit untouched before "waiting" is a lie: the
 * awb-fetch scheduler ticks every minute, so five is already five misses. */
export const PENDING_GRACE_SECONDS = 5 * 60;

export interface StuckGroup {
  cause: StuckCauseKey;
  marketplace: string;
  count: number;
  orderIds: string[];
}

// ponytail: substring heuristic on the raw awb_last_error — these are the
// four phrasings the AWB fetcher actually emits for auth failures today
// (shop account / Lazada / Shopee / TikTok paths). Upgrade path: a `cause`
// code from the BE replaces this table.
const RECONNECT_PATTERNS = ["not connected", "reconnect required", "error_auth", "cipher"];

/** Why this picked order still has no label, reduced to the one action ops
 * can take: reconnect the shop, retry the label request, or wait. An order
 * never attempted past the grace window is not waiting — the scheduler is
 * not picking it up — so it surfaces as a problem instead of a calm state. */
export function stuckCause(
  order: Pick<StuckLike, "awb_attempts" | "awb_last_error" | "stuck_seconds">,
): StuckCauseKey {
  const err = (order.awb_last_error ?? "").toLowerCase();
  if (err === "" && order.awb_attempts === 0) {
    return order.stuck_seconds > PENDING_GRACE_SECONDS ? "never_tried" : "pending";
  }
  if (RECONNECT_PATTERNS.some((p) => err.includes(p))) return "reconnect";
  return "retry";
}

/** Cause × marketplace buckets, biggest first — the "12 orders · Shopee not
 * connected → Reconnect" cards above the stuck table. */
export function groupStuck(orders: StuckLike[]): StuckGroup[] {
  const byKey = new Map<string, StuckGroup>();
  for (const o of orders) {
    const cause = stuckCause(o);
    const key = `${cause}:${o.marketplace}`;
    const g = byKey.get(key) ?? { cause, marketplace: o.marketplace, count: 0, orderIds: [] };
    g.count += 1;
    g.orderIds.push(o.id);
    byKey.set(key, g);
  }
  return [...byKey.values()].sort((a, b) => b.count - a.count);
}
